/**
 * The one Settings navigation model — a stack of targets, root first — and
 * the pocket's navigation context. Page definitions live in
 * `settings-page-definitions`; old tab ids and `?tab=` links resolve here.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import type { LucideIcon } from "lucide-react";
import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import { translate } from "@/i18n";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";
import { isTauri } from "@/lib/is-tauri";
import {
  CARRIED_ARGS,
  REQUIRED_ARGS,
  TAB_TARGETS,
  settingsPageDef,
  type SettingsPageId,
  type SettingsPlatform,
  type SettingsTarget,
} from "./settings-page-definitions";

export {
  SETTINGS_PAGES,
  SETTINGS_ROOT_GROUPS,
  settingsPageDef,
  type SettingsPageDef,
  type SettingsPageId,
  type SettingsPlatform,
  type SettingsTarget,
} from "./settings-page-definitions";

export function currentSettingsPlatform(): SettingsPlatform {
  return isTauri() ? "desktop" : "web";
}

function hasRequiredArgs(target: SettingsTarget): boolean {
  return (REQUIRED_ARGS[target.page] ?? []).every((name) =>
    Boolean(target.args?.[name]),
  );
}

function ancestorArgs(
  page: SettingsPageId,
  args: Record<string, string> | undefined,
): Record<string, string> | undefined {
  const names = REQUIRED_ARGS[page];
  if (!names || !args) return undefined;
  const carried = (CARRIED_ARGS[page] ?? []).filter((name) => args[name]);
  return Object.fromEntries(
    [...names, ...carried].map((name) => [name, args[name] ?? ""]),
  );
}

/** An old tab id or `?tab=` value as a pocket target (spec §8). */
export function settingsTargetFromTab(
  tab: string | null | undefined,
): SettingsTarget {
  if (!tab) return { page: "root" };
  if (["connections-browser", "connections-safari"].includes(tab))
    return { page: "connections" };
  const mapped = TAB_TARGETS.get(tab);
  if (mapped) return { page: mapped };
  const def = settingsPageDef(tab);
  return def && !REQUIRED_ARGS[def.id] ? { page: def.id } : { page: "root" };
}

/** The pages from root to `target`. Unknown, unavailable or incomplete targets open root. */
export function settingsStackFor(
  target: SettingsTarget,
  platform: SettingsPlatform,
): SettingsTarget[] {
  if (["connections-browser", "connections-safari"].includes(target.page)) {
    return platform === "desktop"
      ? settingsStackFor({ page: "connections" }, platform)
      : [{ page: "root" }];
  }
  const def = settingsPageDef(target.page);
  if (
    !def ||
    (def.platforms && !def.platforms.includes(platform)) ||
    !hasRequiredArgs(target)
  ) {
    return [{ page: "root" }];
  }
  const stack: SettingsTarget[] = [target];
  let parent = def.parent;
  while (parent) {
    const args = ancestorArgs(parent, target.args);
    stack.unshift(args ? { page: parent, args } : { page: parent });
    parent = settingsPageDef(parent)?.parent ?? null;
  }
  return stack;
}

export function pushSettingsTarget(
  stack: readonly SettingsTarget[],
  target: SettingsTarget,
): SettingsTarget[] {
  if (["connections-browser", "connections-safari"].includes(target.page))
    return settingsStackFor({ page: "connections" }, currentSettingsPlatform());
  return [...stack, target];
}

/** One level up; the root never pops. */
export function popSettingsTarget(
  stack: readonly SettingsTarget[],
): SettingsTarget[] {
  return stack.length > 1 ? stack.slice(0, -1) : [...stack];
}

/** Identifies page instances; disclosure state stays mounted to animate. */
export function settingsStackKey(stack: readonly SettingsTarget[]): string {
  return stack
    .map(({ page, args }) => {
      const pageArgs =
        page === "preferences-shortcuts"
          ? Object.fromEntries(
              Object.entries(args ?? {}).filter(
                ([name]) => name !== "expanded",
              ),
            )
          : (args ?? {});
      return `${page}${JSON.stringify(pageArgs)}`;
    })
    .join("/");
}

/** The header title in the rendered language; call it at render time. */
export function settingsPageTitle(target: SettingsTarget): string {
  return (
    target.args?.title ||
    settingsPageDef(target.page)?.title ||
    translate(settingsShellMessages, "pageRoot")
  );
}

/** The icon of the root page a page belongs to (search hub rows). */
export function settingsRootIcon(page: SettingsPageId): LucideIcon {
  let def = settingsPageDef(page);
  while (def?.parent && def.parent !== "root") {
    def = settingsPageDef(def.parent);
  }
  return def?.icon ?? CONCEPT_ICONS.settings;
}

/** Navigation inside the open pocket. */
export interface SettingsNav {
  /** The open pages, root first. */
  stack: readonly SettingsTarget[];
  /** Drill into a child page. */
  push: (target: SettingsTarget) => void;
  /** Jump to a page on another branch; back then walks that page's parents. */
  open: (target: SettingsTarget) => void;
  /** One level up; at root this closes the pocket. */
  back: () => void;
  close: () => void;
}

export const SettingsNavContext = createContext<SettingsNav | null>(null);

export function useSettingsNav(): SettingsNav {
  const nav = useContext(SettingsNavContext);
  if (!nav)
    throw new Error("useSettingsNav must be used inside the Settings pocket");
  return nav;
}

/** Asks before a page with unsaved edits is left; calling `leave` goes on. */
export type SettingsLeaveGuard = (leave: () => void) => void;

/** Registers the open page's leave guard with its pocket; no pocket, no guard. */
export const SettingsLeaveGuardContext = createContext<
  (guard: SettingsLeaveGuard | null) => void
>(() => {});

/** The pocket's navigation: every move off the open page asks its leave guard first. */
export function useSettingsPocketNav(
  stack: readonly SettingsTarget[],
  onStackChange: (stack: SettingsTarget[]) => void,
  onClose: () => void,
): {
  nav: SettingsNav;
  registerLeaveGuard: (guard: SettingsLeaveGuard | null) => void;
} {
  const guard = useRef<SettingsLeaveGuard | null>(null);
  const registerLeaveGuard = useCallback((next: SettingsLeaveGuard | null) => {
    guard.current = next;
  }, []);
  const nav = useMemo<SettingsNav>(() => {
    const leave = (go: () => void) =>
      guard.current ? guard.current(go) : go();
    return {
      stack,
      push: (target) =>
        leave(() => onStackChange(pushSettingsTarget(stack, target))),
      open: (target) =>
        leave(() =>
          onStackChange(settingsStackFor(target, currentSettingsPlatform())),
        ),
      back: () =>
        leave(() =>
          stack.length > 1
            ? onStackChange(popSettingsTarget(stack))
            : onClose(),
        ),
      close: () => leave(onClose),
    };
  }, [onClose, onStackChange, stack]);
  return { nav, registerLeaveGuard };
}

/** While `guard` is set, leaving the page asks it first. The returned
 * `release` lets the page leave without asking, after it saved or discarded. */
export function useSettingsLeaveGuard(
  guard: SettingsLeaveGuard | null,
): () => void {
  const register = useContext(SettingsLeaveGuardContext);
  useEffect(() => {
    if (!guard) return;
    register(guard);
    return () => register(null);
  }, [guard, register]);
  return useCallback(() => register(null), [register]);
}
